<template lang="pug">
section.users-management
  header-bar(
    :title="$t('users.title')"
    :description="$t('users.description')")
    ods-button(type="primary" @click="createUser") {{ $t('users.createUser') }}
  div.users-management__wrapper
    div.users-management__header
      total-counter.ods-mr-2(:text="$t('users.totalUsers')" :value="allUsers.length")
      ods-input(
        v-model="search"
        size="mega"
        :placeholder="$t('users.searchUsers')"
        prefix-icon="ods-icon-search"
        clearable)
    ods-table(
      :data="users"
      style="width: 100%"
      size="small")
      ods-table-column(
        prop="username"
        :label="$tc('users.user', 1)"
        :min-width="140"
        sortable)
      ods-table-column(
        prop="fullName"
        :label="$t('users.fullName')"
        :min-width="180"
        sortable)
      ods-table-column(
        prop="mail"
        :label="$t('users.email')"
        :min-width="180"
        sortable)
      ods-table-column(
        prop="role"
        :label="$tc('users.role', 1)"
        :width="180"
        sortable)
      ods-table-column(
        prop="creationDate"
        :label="$t('users.creationDate')"
        :width="160"
        sortable
        :sort-method="handleSortDate")
        template(slot-scope="scope")
          span {{ scope.row.creationDate | formatDate }}
      ods-table-column(
        prop="active"
        :label="$t('users.state')"
        :width="120"
        sortable)
        template(slot-scope="scope")
          div.users-management__status(:class="{'users-management__status--connected': scope.row.active}")
            ods-badge(is-dot :type="scope.row.active ? 'success' : 'default'")
            span.users-management__status-text {{ scope.row.active ? $t('users.active') : $t('users.inactive') }}
      ods-table-column(align="right" :width="80")
        template(slot-scope="scope")
          template(v-if="scope.row.role != 'MASTER'")
            div.users-management__actions
              ods-tooltip.ods-mr-2(
                :content="$t('userRoles.admin.editUser.title')"
                placement="top")
                ods-icon.users-management__icon.users-management__icon--edition(
                  name="pencil"
                  size="15"
                  @click.native="editUser(scope.row)")
              ods-tooltip.ods-mr-2(
                :content="$t('notifications.delete')"
                placement="top")
                ods-icon.users-management__icon.users-management__icon--delete(
                  name="delete"
                  size="15"
                  @click.native="openDeleteUserModal(scope.row.username)")
    ods-pagination(
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page.sync="pagination.currentPage"
      :page-sizes="[10, 20, 40, 100]"
      :page-size="pagination.currentPageSize"
      layout="total, sizes, ->, jumper, prev, pager, next"
      :total="pagination.totalPageSize")
  //- Delete user
  ods-dialog(
    :title="$t('users.deleteUser')"
    custom-class="users-management__delete"
    :visible.sync="deleteModalVisible"
    :show-close="false"
    width="480px")
    span {{ $t('users.deleteUserDesc') }}
    .dialog-footer(slot="footer")
      ods-button(type="neutral" @click="deleteModalVisible = false") {{ $t('users.cancel') }}
      ods-button(type="destructive" @click="submitDeleteUser") {{ $t('users.confirm') }}
</template>

<script>
import { mapActions } from 'vuex'
import HeaderBar from '@/components/shared/HeaderBar'
import TotalCounter from '@/components/shared/TotalCounter'
import { searchByFields } from '@/utils/helpers'
export default {
  name: 'UsersManagementView',
  components: {
    HeaderBar,
    TotalCounter
  },
  data () {
    return {
      search: '',
      allUsers: [],
      users: [],
      deleteModalVisible: false,
      currentUser: '',
      pagination: {
        currentPage: 1,
        currentPageSize: 10,
        totalPageSize: 0
      }
    }
  },
  watch: {
    search (val) {
      const users = searchByFields(this.allUsers, ['username', 'fullName', 'mail', 'role'], val)
      this.pagination.totalPageSize = users.length
      this.pagination.currentPage = 1
      this.users = users.slice(0, this.pagination.currentPageSize)
    }
  },
  methods: {
    ...mapActions('usersManagement', ['getUsers', 'deleteUser']),
    createUser () {
      this.$router.push({ name: 'UsersManagementForm' })
    },
    editUser (user) {
      this.$router.push({ name: 'UsersManagementForm', params: { username: user.username } })
    },
    openDeleteUserModal (username) {
      this.currentUser = username
      this.deleteModalVisible = true
    },
    submitDeleteUser () {
      const loading = this.$loading({
        target: '.users-management__delete'
      })
      this.deleteUser(this.currentUser).then(() => {
        return this.getUsers(sessionStorage.getItem('VUE_APP_KEYCLOAK_CLIENT_ID'))
      }).then(response => {
        this.setPagination(response)
        loading.close()
        this.deleteModalVisible = false
        this.$notify({
          title: this.$t('users.notification.userDeleted', { user: this.currentUser }),
          type: 'success',
          position: 'top-right',
          duration: 5000
        })
      }).catch(err => {
        loading.close()
        return err
      })
    },
    handleSortDate (a, b) {
      return new Date(b.creationDate) - new Date(a.creationDate)
    },
    // PAGINATION
    handleSizeChange (val) {
      this.pagination.currentPageSize = val
      this.users = this.allUsers.slice(0, this.pagination.currentPageSize)
    },
    handleCurrentChange (val) {
      this.users = this.allUsers.slice(this.pagination.currentPageSize * val - this.pagination.currentPageSize, this.pagination.currentPageSize * val)
    },
    // END PAGINATION
    setPagination (users) {
      this.allUsers = users
      this.pagination.totalPageSize = this.allUsers.length
      this.users = this.allUsers.slice(0, this.pagination.currentPageSize)
      this.pagination.currentPage = 1
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (from.name === 'UsersManagementForm') {
        if (to.params.username) {
          let title
          if (to.params.action === 'createUser') {
            title = vm.$t('users.notification.userCreated', { user: to.params.username })
          } else {
            title = vm.$t('users.notification.userUpdated', { user: to.params.username })
          }
          vm.$notify({
            title: title,
            type: 'success',
            position: 'top-right',
            duration: 5000
          })
        }
      }
    })
  },
  mounted () {
    const loading = this.$loading({
      target: '.users-management'
    })
    this.getUsers(sessionStorage.getItem('VUE_APP_KEYCLOAK_CLIENT_ID')).then(response => {
      this.setPagination(response)
      loading.close()
    }).catch(err => {
      loading.close()
      return err
    })
  }
}
</script>

<style lang="scss" scoped>
.users-management {
  background-color: var(--color-bg-primary);
  min-height: 100%;
  &__wrapper {
    padding: $space-200;
    background-color: var(--color-bg-primary);
  }
  &__header {
    margin-bottom: $space-150;
    display: flex;
    align-items: center;
  }
  &__status {
    display: flex;
    align-items: center;
    &-text {
      @include txt-body-300;
      margin-left: $space-100;
      color: var(--color-txt-secondary);
    }
    &--connected {
      .users__status-text {
        color: var(--color-status-success-400);
      }
    }
  }
  &__actions {
    display: flex;
    gap: $space-150;
  }
  &__icon {
    cursor: pointer;
    &--edition {
      color: var(--color-icon-interactive);
    }
    &--delete {
      color: var(--color-bg-destructive);
    }
  }
}
</style>
